import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logError, logInfo } from '../logger';
import * as turf from '@turf/turf';
import { COLLECTIONS } from '../database/collections';

/**
 * Gets structures with height restrictions along a route
 * @param {Object} routeLineString - Turf.js linestring representing the route
 * @param {number} bufferDistance - Buffer distance in kilometers
 * @param {Object} filters - Additional filters
 * @returns {Promise<Array>} - Array of structures along the route
 */
export const getHauteursLibresAlongRoute = async (routeLineString, bufferDistance = 0.5, filters = {}) => {
  try {
    // Get all structures
    const structuresRef = collection(db, 'hauteurs_libres');
    let q = structuresRef;
    
    // Apply filters if provided
    if (filters.minHauteur) {
      q = query(q, where('hauteur', '>=', parseFloat(filters.minHauteur)));
    }
    
    if (filters.maxHauteur) {
      q = query(q, where('hauteur', '<=', parseFloat(filters.maxHauteur)));
    }
    
    if (filters.route) {
      q = query(q, where('route', '==', filters.route));
    }
    
    const snapshot = await getDocs(q);
    
    // Filter out metadata document
    const structures = snapshot.docs
      .filter(doc => doc.id !== 'metadata')
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    
    logInfo(`Retrieved ${structures.length} structures from Firestore`);
    
    // If we don't have a route, return all structures
    if (!routeLineString) {
      return structures;
    }
    
    let result = structures;
    
    // If we have a route, filter by buffer distance for structures with coordinates
    if (routeLineString) {
      // Create a buffer around the route
      const buffered = turf.buffer(routeLineString, bufferDistance, { units: 'kilometers' });
      
      // Separate structures with and without coordinates
      const structuresWithCoordinates = structures.filter(structure => 
        structure.coordinates?.latitude && structure.coordinates?.longitude
      );
      
      const structuresWithoutCoordinates = structures.filter(structure => 
        !structure.coordinates?.latitude || !structure.coordinates?.longitude
      );
      
      logInfo(`${structuresWithCoordinates.length} structures have coordinates, ${structuresWithoutCoordinates.length} don't`);
      
      // Filter structures within buffer
      const structuresAlongRoute = structuresWithCoordinates.filter(structure => {
        try {
          const point = turf.point([structure.coordinates.longitude, structure.coordinates.latitude]);
          const isInBuffer = turf.booleanPointInPolygon(point, buffered);
          
          if (isInBuffer) {
            // Calculate distance from route
            const nearestPoint = turf.nearestPointOnLine(
              routeLineString, 
              point, 
              { units: 'kilometers' }
            );
            
            // Add distance to structure
            structure.distanceFromRoute = nearestPoint.properties.dist * 1000; // Convert to meters
            return true;
          }
          
          return false;
        } catch (error) {
          logError(`Error filtering structure ${structure.reference}:`, error);
          return false;
        }
      });
      
      // Include structures along the route
      result = structuresAlongRoute;
      
      // Add properties for compatibility
      result = result.map(structure => {
        // Ensure properties object exists
        if (!structure.properties) {
          structure.properties = {};
        }
        
        // Add clearance to properties if not already there
        if (!structure.properties.clearance && structure.hauteur) {
          structure.properties.clearance = structure.hauteur;
        }
        
        // Add municipality to properties if not already there
        if (!structure.properties.municipality && structure.municipalite) {
          structure.properties.municipality = structure.municipalite;
        }
        
        // Add route to properties if not already there
        if (!structure.properties.route && structure.route) {
          structure.properties.route = structure.route;
        }
        
        // Add name if not already there
        if (!structure.name && structure.obstacle) {
          structure.name = structure.obstacle;
        }
        
        return structure;
      });
    }
    
    logInfo(`Found ${result.length} structures along route`);
    return result;
  } catch (error) {
    logError('Failed to get structures along route:', error);
    throw error;
  }
};

/**
 * Gets the minimum height for a structure
 * @param {Object} structure - Structure data
 * @returns {number} - Minimum height
 */
export const getMinimumHeight = (structure) => {
  // If we have details, find the minimum height across all measurements
  if (structure?.details && Array.isArray(structure.details)) {
    const heights = structure.details
      .flatMap(detail => [
        detail.gauche,
        detail.centre_gauche,
        detail.centre,
        detail.centre_droit,
        detail.droite
      ])
      .filter(height => height !== null && height !== undefined);
    
    if (heights.length > 0) {
      return Math.min(...heights);
    }
  }
  
  // Otherwise, use the main height
  return structure?.hauteur;
};

/**
 * Checks if a vehicle can pass under a structure
 * @param {Object} structure - Structure data
 * @param {number} vehicleHeight - Vehicle height in meters
 * @param {number} safetyMargin - Safety margin in meters
 * @returns {boolean} - True if the vehicle can pass
 */
export const canVehiclePass = (structure, vehicleHeight, safetyMargin = 0.1) => {
  const minHeight = getMinimumHeight(structure);
  return minHeight >= (vehicleHeight + safetyMargin);
};